import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-4ad06f18"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "housing-search__section-wrapper" }
const _hoisted_2 = { class: "housing-search__section" }
const _hoisted_3 = { class: "housing-search__section__date" }
const _hoisted_4 = { class: "housing-search__title" }
const _hoisted_5 = { class: "housing-search__date-controls" }

import DatePicker from '@/components/DatePicker.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { computed, PropType, reactive, ref, watch } from 'vue'
import {
  useFetchCompanyPeriodAvailability,
  useQueryCompanyHousingDateAvailabilityList,
} from '@/queries/public/services'
import { formatDateTime } from '@/utils/formatters'
import {
  CompanyHousingDateAvailability,
  PeriodAvailability,
  PeriodAvailabilityPayload,
  PublicServiceDetails,
} from '@/models/public/service.model'
import format from 'date-fns/format'
import { HousingAvailabilityPayload } from '@/models/public/order.model'
import subDays from 'date-fns/subDays'
import addDays from 'date-fns/addDays'
import parseISO from 'date-fns/parseISO'
import { getValidationErrorData } from '@/utils/errors'
import { watchEffect } from 'vue'
import isSameDay from 'date-fns/isSameDay'
import startOfToday from 'date-fns/startOfToday'
import { serviceResultCardCommonProps } from '@/enums/public'
import { getYearMonthAsInterval } from '@/utils/calendar'


import { YearMonth } from '@/models/calendar.model'
import { StringDate } from '@/models/shared.model'
import { getYearMonthForDate } from '@/utils/calendar'

export interface State {
  periodPickerYearMonth: YearMonth
}

export const initializePickerYearMonth = (
  date: Date | StringDate | null,
): YearMonth => {
  if (!date) {
    date = new Date()
  } else {
    // In case of StringDate
    date = new Date(date)
  }

  return getYearMonthForDate(date)
}

export const stateFieldMonthYearChange =
  (state: object, field: string) =>
  ({ month, year }: { month: number; year: number }) => {
    Object.assign(state, {
      [field]: { year, month },
    })
  }

export default /*#__PURE__*/_defineComponent({
  __name: 'HousingResultCardSearch',
  props: {
  modelValue: {
    type: Object as PropType<HousingAvailabilityPayload>,
    required: true,
  },
  service: {
    type: Object as PropType<PublicServiceDetails>,
    required: true,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  groupService: serviceResultCardCommonProps.groupService,
},
  emits: ['update:modelValue', 'periodAvailability'],
  setup(__props, { emit }) {

const props = __props





// We update parent based on these changes
const selectedPeriod = ref<StringDate[] | null>(null)
// For internal usage, to show disabled dates based on period start or end selection
const _periodStart = ref<StringDate | null>(props.modelValue.check_in)
const _periodEnd = ref<StringDate | null>(props.modelValue?.check_out)

const periodAvailability = ref<PeriodAvailability | undefined>(undefined)

const errors = ref<Record<string, unknown> | undefined>(undefined)

const state = reactive<State>({
  // What month is open in datepicker. We use it to fetch and show availability per date.
  periodPickerYearMonth: initializePickerYearMonth(props.modelValue.check_in),
})

/**
 * Availabilities query to show date availabilities on the fly in the START date picker.
 */
const {
  data: calendarAvailability,
  isFetching: isLoadingCalendarAvailability,
} = useQueryCompanyHousingDateAvailabilityList(
  computed(() => {
    const periodPickerInterval = getYearMonthAsInterval(
      state.periodPickerYearMonth,
    )
    let startDate = periodPickerInterval.start
    // Necessary for when we change calendar period after choosing period start
    if (_periodStart.value && _periodStart.value < startDate) {
      startDate = _periodStart.value
    }
    const endDate = periodPickerInterval.end
    return {
      company: props.service.company.id,
      // If we don't specify only this housing it will try to load for all housings, which is unnecessary.
      services: [props.service.hash_id],
      // Because stay period end's options are based on previous day's availability
      // we need to fetch the day before calendar's start as well
      start: format(subDays(startDate, 1), 'yyyy-MM-dd'),
      end: format(endDate, 'yyyy-MM-dd'),
      group_service: props.groupService,
    }
  }),
)

/**
 * START date picker's available dates based on availability endpoint information which is filtered and mapped to list of dates.
 */
const startAvailableDates = computed<StringDate[]>(() => {
  const availabilities = calendarAvailability.value || []

  return availabilities
    .filter((item: CompanyHousingDateAvailability) => {
      return item.is_available
    })
    .map((item: CompanyHousingDateAvailability) => item.date)
})

/**
 * END date picker's available dates based on availability endpoint information which is filtered and mapped to list of dates.
 *
 * For period end the date is available when the previous date is available.
 * That means given date's availability state actually represents next day's availabilty as reservation end date.
 * That is we requested availability for the day before calendar's first shown date and why we add a day here to all available dates.
 */
const endAvailableDates = computed<StringDate[]>(() => {
  const startDate = _periodStart.value
    ? parseISO(_periodStart.value)
    : startOfToday()
  let nextExpectedDate = addDays(startDate, 1)

  const availabilities = calendarAvailability.value || []
  const endDates = availabilities
    .filter((item: CompanyHousingDateAvailability) => {
      return item.is_available
    })
    .map((item: CompanyHousingDateAvailability) =>
      addDays(parseISO(item.date), 1),
    )

  // Start date must be in the options for DatePicker picking to work
  const formattedValidEndDates: StringDate[] = [format(startDate, 'yyyy-MM-dd')]
  for (const date of endDates) {
    if (isSameDay(date, nextExpectedDate)) {
      formattedValidEndDates.push(format(date, 'yyyy-MM-dd'))
      nextExpectedDate = addDays(nextExpectedDate, 1)
    }
  }
  return formattedValidEndDates
})

const getPeriodDisabledDates = (date: Date) => {
  const dateStr: StringDate = formatDateTime(date)
  if (!!_periodStart.value && !_periodEnd.value) {
    return !endAvailableDates.value.includes(dateStr)
  }
  return !startAvailableDates.value.includes(dateStr)
}

const periodErrors = computed(() => {
  const _errors = []
  _errors.push(...(errors.value?.start ?? []))
  _errors.push(...(errors.value?.check_in ?? []))
  _errors.push(...(errors.value?.end ?? []))
  _errors.push(...(errors.value?.check_out ?? []))
  return _errors
})

const updatePeriodStart = (value) => {
  _periodStart.value = value ? format(value, 'yyyy-MM-dd') : null
  updatePeriodEnd(null)
}

const updatePeriodEnd = (value) => {
  _periodEnd.value = value ? format(value, 'yyyy-MM-dd') : null
}

const updateSelectedPeriod = (period: Date[] | null) => {
  if (period === null) {
    selectedPeriod.value = null
  }

  selectedPeriod.value = [
    format(period[0], 'yyyy-MM-dd'),
    format(period[1], 'yyyy-MM-dd'),
  ]
}

const startMonthYearChange = stateFieldMonthYearChange(
  state,
  'periodPickerYearMonth',
)

const {
  mutateAsync: fetchPeriodAvailability,
  isLoading: isLoadingPeriodAvailability,
} = useFetchCompanyPeriodAvailability()

const searchAvailability = async () => {
  if (!selectedPeriod.value) {
    return
  }

  errors.value = undefined
  const payload: PeriodAvailabilityPayload = {
    company: props.service.company?.id,
    services: [props.service.hash_id],
    start: selectedPeriod.value[0],
    end: selectedPeriod.value[1],
  }

  fetchPeriodAvailability(payload)
    .then((data) => {
      // we only care about current service's availability.
      // there should be 0-1 items related to this service
      periodAvailability.value = data.find(
        (item) => item.service.id === props.service.id,
      )
    })
    .catch((error) => {
      errors.value = getValidationErrorData(error)
    })
}

watch(
  () => props.modelValue,
  () => {
    _periodStart.value = props.modelValue.check_in
    _periodEnd.value = props.modelValue.check_out

    if (_periodStart.value && _periodEnd.value) {
      selectedPeriod.value = [_periodStart.value, _periodEnd.value]
    } else {
      selectedPeriod.value = null
    }
  },
  { immediate: true },
)

watch(
  selectedPeriod,
  (newVal, oldVal) => {
    if (newVal !== null && newVal !== oldVal) {
      searchAvailability()
    }
  },
  {
    immediate: true,
  },
)

watchEffect(() => {
  emit('update:modelValue', {
    ...props.modelValue,
    check_in: selectedPeriod.value?.[0] ?? null,
    check_out: selectedPeriod.value?.[1] ?? null,
  })
})

watchEffect(() => {
  emit('periodAvailability', periodAvailability.value)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h4", _hoisted_4, _toDisplayString(_ctx.$t('whenAreYouGoing')), 1),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(DatePicker, {
            range: "",
            "model-value": selectedPeriod.value,
            "min-range": 1,
            "no-disabled-range": false,
            name: "stayPeriod",
            errors: _unref(periodErrors),
            class: _normalizeClass(["housing-search__input", {
              'housing-search__input--error': _unref(periodErrors).length > 0,
            }]),
            placeholder: _ctx.$t('period'),
            "disabled-dates": getPeriodDisabledDates,
            "month-change-on-scroll": false,
            "month-change-on-arrows": false,
            "keep-action-row": "",
            disabled: __props.isDisabled,
            "onUpdate:modelValue": updateSelectedPeriod,
            onRangeStart: updatePeriodStart,
            onRangeEnd: updatePeriodEnd,
            onUpdateMonthYear: _unref(startMonthYearChange)
          }, {
            "action-preview": _withCtx(() => [
              _createElementVNode("div", {
                class: _normalizeClass(["housing-search__availabilities-loading", {
                  'visibility-hidden': !_unref(isLoadingCalendarAvailability),
                }])
              }, [
                _createVNode(LoadingSpinner, { size: 16 }),
                _createElementVNode("span", null, _toDisplayString(_ctx.$t('bookingForm.loadingDatePickerTimeslots')), 1)
              ], 2)
            ]),
            "action-buttons": _withCtx(() => []),
            _: 1
          }, 8, ["model-value", "errors", "class", "placeholder", "disabled", "onUpdateMonthYear"])
        ])
      ])
    ])
  ]))
}
}

})